import { ref, onMounted, onUnmounted } from 'vue'

const DEFAULT_INACTIVITY_MS = (parseInt(process.env.VUE_APP_INACTIVITY_MINUTES) || 55) * 60 * 1000

export default function useInactivityDetector(inactivityTime = DEFAULT_INACTIVITY_MS) {
  const showModal = ref(false)
  
  let inactivityTimer = null
  // El timer solo debe reaccionar a actividad del usuario cuando hay una
  // sesión activa. Sin esto, cualquier evento de window (mousemove, click,
  // keypress) en la pantalla de login rearma el conteo de inactividad sin
  // que exista sesión alguna.
  let enabled = false
  const events = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click']

  // Los navegadores retrasan (throttle) los setTimeout/setInterval de
  // pestañas en background — a veces por decenas de minutos. Sin un
  // registro persistido de la última actividad real, un usuario que vuelve
  // a una pestaña ya inactiva por más tiempo del permitido e interactúa de
  // inmediato dispararía resetTimer() y reiniciaría el conteo en silencio,
  // como si nunca hubiera estado inactivo.
  const ACTIVITY_STORAGE_KEY = '__last_activity_at'

  /**
   * Resetea el timer de inactividad (no-op si el detector está deshabilitado).
   * Si la actividad llega después de que ya se cumplió inactivityTime desde
   * el último registro (setTimeout retrasado por throttling), no reinicia
   * el conteo: muestra el aviso directamente, igual que si el setTimeout
   * hubiera disparado a tiempo.
   */
  const resetTimer = () => {
    if (!enabled) return

    const lastActivityAt = Number(sessionStorage.getItem(ACTIVITY_STORAGE_KEY)) || Date.now()
    const elapsedSinceLastActivity = Date.now() - lastActivityAt

    sessionStorage.setItem(ACTIVITY_STORAGE_KEY, String(Date.now()))

    if (elapsedSinceLastActivity >= inactivityTime) {
      clearTimeout(inactivityTimer)
      showModal.value = true
      return
    }

    clearTimeout(inactivityTimer)
    startTimer()
  }

  /**
   * Inicia el timer de inactividad y habilita la detección de actividad
   */
  const startTimer = () => {
    enabled = true
    sessionStorage.setItem(ACTIVITY_STORAGE_KEY, String(Date.now()))
    // Limpiar cualquier timer anterior antes de iniciar uno nuevo
    clearTimeout(inactivityTimer)

    inactivityTimer = setTimeout(() => {
      showModal.value = true
    }, inactivityTime)
  }

  /**
   * Detiene el timer de inactividad y deshabilita la detección de actividad
   */
  const stopTimer = () => {
    enabled = false
    sessionStorage.removeItem(ACTIVITY_STORAGE_KEY)
    clearTimeout(inactivityTimer)
    inactivityTimer = null
  }

  /**
   * Cierra el modal de inactividad
   */
  const closeModal = () => {
    showModal.value = false
  }

  /**
   * Agregar listeners de eventos
   */
  const addEventListeners = () => {
    events.forEach(event => {
      window.addEventListener(event, resetTimer)
    })
  }

  /**
   * Remover listeners de eventos
   */
  const removeEventListeners = () => {
    events.forEach(event => {
      window.removeEventListener(event, resetTimer)
    })
  }

  /**
   * Si el usuario vuelve a la pestaña sin generar un evento de actividad
   * (p.ej. solo cambia de pestaña y se queda mirando), el setTimeout pudo
   * haberse retrasado por throttling sin que ningún listener de actividad
   * lo detecte. Revisar el tiempo real transcurrido al recuperar
   * visibilidad, sin esperar a que el usuario interactúe.
   */
  const handleVisibilityChange = () => {
    if (!enabled) return
    if (document.visibilityState !== 'visible') return

    const lastActivityAt = Number(sessionStorage.getItem(ACTIVITY_STORAGE_KEY)) || Date.now()
    const elapsedSinceLastActivity = Date.now() - lastActivityAt

    if (elapsedSinceLastActivity >= inactivityTime) {
      clearTimeout(inactivityTimer)
      showModal.value = true
    }
  }

  // Inicializar al montar - solo agregamos los listeners
  // El timer se controla desde App.vue mediante el watch de rutas
  onMounted(() => {
    addEventListeners()
    document.addEventListener('visibilitychange', handleVisibilityChange)
  })

  // Limpiar al desmontar
  onUnmounted(() => {
    removeEventListeners()
    document.removeEventListener('visibilitychange', handleVisibilityChange)
    stopTimer()
  })

  return {
    showModal,
    startTimer,
    stopTimer,
    resetTimer,
    closeModal
  }
}

